/**
 * Copyright © 2025 Farmlands Co-operative Society Limited. All rights reserved.
 *
 * This document contains confidential information and is the property of Farmlands.
 * Unauthorised reproduction or distribution of any part of this publication is prohibited without written consent.
 */

// Custom hook to fetch product availabilities
import { debounce } from "lodash";
import { useCallback, useEffect, useState } from "react";

import { AppDispatch, useAppDispatch } from "../../../../store/rootStore";
import { CartItem } from "../../../cart/storage/cart.types";
import { getStockAvailabilities, Product } from "../../storage";
import { StocksSlice } from "../../storage/stocks.slice";

export const mergeStockWithProducts = async (
  products: Product[] | CartItem[],
  dispatch: AppDispatch
) => {
  const productIds = products.map(({ productId }) => productId);
  const items = await getStockAvailabilities({ productIds });
  if (items && items.length > 0) {
    dispatch(StocksSlice.actions.addAvailabilities(items));
  }

  return products.map((product) => {
    const stockAvailabilities = items?.filter((item) => item.productId === product.productId);
    return { ...product, stockAvailabilities };
  });
};

interface Props {
  products: Product[] | CartItem[];
}
const useStockAvailabilities = ({ products }: Props) => {
  const dispatch = useAppDispatch();

  const [productsWithStock, setProductsWithStock] = useState<Product[] | CartItem[] | undefined>(
    undefined
  );

  const debouncedMergeProducts = useCallback(
    debounce(async (products: Product[] | CartItem[]) => {
      const mergedData = await mergeStockWithProducts(products, dispatch);
      if (mergedData) {
        setProductsWithStock(mergedData);
      }
    }, 300),
    [dispatch]
  );

  useEffect(() => {
    if (products.length > 0) {
      setProductsWithStock(undefined);
      debouncedMergeProducts(products);
    } else {
      setProductsWithStock([]);
    }
  }, [products, debouncedMergeProducts]);

  return productsWithStock ?? products;
};

export default useStockAvailabilities;
